import { writable, type Writable } from 'svelte/store';

// Runtime configuration from window.__APP_CONFIG__ (injected at container startup)
// Falls back to import.meta.env for local development
const API_BASE_URL =
  window.__APP_CONFIG__?.apiUrl || import.meta.env.VITE_API_URL || 'http://localhost:3000';

export interface Permissions {
  canDeleteDocuments: boolean;
  canReprocessDocuments: boolean;
  canUploadDocuments: boolean;
}

export const permissionsStore: Writable<Permissions | null> = writable(null);

class PermissionsService {
  private baseUrl: string;

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl;
  }

  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem('auth_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  async fetchPermissions(): Promise<void> {
    try {
      const apiUrl = `${this.baseUrl}/api/v1/permissions`;
      const response = await fetch(apiUrl, {
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        console.error('Failed to fetch permissions:', response.status, response.statusText);
        permissionsStore.set(null); // Clear permissions on error
        return;
      }

      const responseBody = await response.json();

      if (responseBody && responseBody.permissions) {
        const permissions: Permissions = responseBody.permissions;
        permissionsStore.set(permissions);
      } else {
        console.error('API response did not contain expected "permissions" object.', responseBody);
        permissionsStore.set(null);
      }
    } catch (error) {
      console.error('Error fetching permissions:', error);
      permissionsStore.set(null); // Clear permissions on error
    }
  }

  canDeleteDocument(): boolean {
    let can = false;
    permissionsStore.subscribe((p) => {
      can = p?.canDeleteDocuments || false;
    })();
    return can;
  }

  canReprocessDocument(): boolean {
    let can = false;
    permissionsStore.subscribe((p) => {
      can = p?.canReprocessDocuments || false;
    })();
    return can;
  }
}

export const permissionsApi = new PermissionsService();
